import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { getQueryParams } from "@/utils/httpRequest";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics } from "@/utils/analytics";
import { baseUrl, onPrepareHeaders } from "@/redux/services/helpers";
import { showNotification } from "@/redux/actions/notification";
import { requestCloseSlideOver } from "@/redux/actions/slide_over";
import { requestCloseModal } from "@/redux/actions/modal";
import { lenderCallLogService } from "@/redux/services/lenderCallLogService";
import { setProcessingState } from "@/redux/reducers/modalConfirmation";
import { loanService } from "@/redux/services/loanService";
import { LENDER_ANALYTIC_EVENTS } from "@/features/Lender/utils/lenderAnalyticEvents";
import { REDUCER_PATH, RELATED_TAGS, LENDER_SERVICE_TAG } from "@/features/Lender/utils/lenderServiceTags";
import { CONTACT_SERVICE_TAG } from "@/features/Contacts/utils/contactServiceTags";
import { contactService } from "@/features/Contacts/store/contactService";
import { LIST_SERVICE_TAG } from "@/shared/store/service/commonServiceTags";

const lenderService = createApi({
  reducerPath: REDUCER_PATH,
  tagTypes: RELATED_TAGS,
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    getLenders: builder.query({
      query: ({ params }) => `lenders?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [
              ...result.items.map(({ lenderId }) => ({ type: LENDER_SERVICE_TAG, lenderId })),
              { type: LENDER_SERVICE_TAG, id: LIST_SERVICE_TAG },
            ]
          : [{ type: LENDER_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted({ params, setParams }, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (!data.items.length && params.page > 1) {
            setParams(prevParams => ({ ...prevParams, page: prevParams.page - 1 }));
          }
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    getLender: builder.query({
      query: id => `lenders/${id}`,
      providesTags: (result, error, id) => [{ type: LENDER_SERVICE_TAG, id }],
    }),
    addLender: builder.mutation({
      query: body => ({
        url: "lenders",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: LENDER_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(LENDER_ANALYTIC_EVENTS.LENDER_CREATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lender has been created!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
          dispatch(lenderCallLogService.util.invalidateTags(["LenderCallLog"]));
          dispatch(contactService.util.invalidateTags([CONTACT_SERVICE_TAG]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    updateLender: builder.mutation({
      query: ({ lenderId: id, ...body }) => ({
        url: `lenders/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: LENDER_SERVICE_TAG, id },
        { type: LENDER_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(LENDER_ANALYTIC_EVENTS.LENDER_UPDATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lender has been updated!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(lenderCallLogService.util.invalidateTags(["LenderCallLog"]));
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
          dispatch(contactService.util.invalidateTags([CONTACT_SERVICE_TAG]));
          dispatch(requestCloseSlideOver());
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    deleteLender: builder.mutation({
      query(id) {
        return {
          url: `lenders/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [{ type: LENDER_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(LENDER_ANALYTIC_EVENTS.LENDER_DELETED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lender has been deleted!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(loanService.util.invalidateTags(["LoanLendersHeatMap"]));
          dispatch(lenderCallLogService.util.invalidateTags(["LenderCallLog"]));
          dispatch(contactService.util.invalidateTags([CONTACT_SERVICE_TAG]));
          dispatch(requestCloseSlideOver());
          dispatch(requestCloseModal());
        } catch (err) {
          dispatch(setProcessingState(false));
          Bugsnag.notify(err);
        }
      },
    }),
    requestLenderParametersUpdate: builder.mutation({
      query(id) {
        return {
          url: `lenders/${id}/programs/update-request`,
          method: "POST",
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));

          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.BROKER_REQUESTED_LENDER_PARAMETERS_UPDATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Lender has been notified!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(requestCloseModal());
          dispatch(contactService.util.invalidateTags([CONTACT_SERVICE_TAG]));
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),
  }),
});

const {
  useGetLendersQuery,
  useGetLenderQuery,
  useAddLenderMutation,
  useUpdateLenderMutation,
  useDeleteLenderMutation,
  useRequestLenderParametersUpdateMutation,
} = lenderService;

export {
  LENDER_SERVICE_TAG,
  useGetLendersQuery,
  useGetLenderQuery,
  useAddLenderMutation,
  useUpdateLenderMutation,
  useDeleteLenderMutation,
  useRequestLenderParametersUpdateMutation,
  lenderService,
};
